/**
 * GST Authenticate Modal (v2) — GSTIN is now PICKED from the company's
 * already-registered GSTINs (Settings → Company GSTINs), not typed in.
 * If none configured, users are pointed to Settings first.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Send, Wrench, Settings2, ExternalLink, AlertTriangle } from "lucide-react";

export default function GstAuthenticateModal({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("otp");
  const [companyGstins, setCompanyGstins] = useState([]);
  const [gstin, setGstin] = useState("");
  const [gstUsername, setGstUsername] = useState("");
  const [otp, setOtp] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [sek, setSek] = useState("");
  const [busy, setBusy] = useState(false);
  const [portalError, setPortalError] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/company/gstins").then((r) => {
      const items = r.data.items || [];
      setCompanyGstins(items);
      if (items.length > 0 && !gstin) {
        const def = items.find((x) => x.is_default) || items[0];
        setGstin(def.gstin);
      }
    }).catch(() => setCompanyGstins([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => { setStep(1); setMode("otp"); setGstin(""); setGstUsername(""); setOtp(""); setAuthToken(""); setSek(""); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const sendOtp = async () => {
    if (gstin.length !== 15) { toast.error("Pick a GSTIN"); return; }
    if (!gstUsername) { toast.error("GST portal username required"); return; }
    setBusy(true); setPortalError("");
    try {
      const r = await api.post("/gst-reports/auth/otp-request",
        { gstin, gst_username: gstUsername });
      toast.success(`OTP sent (txn=${r.data.txn?.slice(0, 10)}…)`);
      setStep(2);
    } catch (e) {
      const detail = e?.response?.data?.detail || "OTP dispatch failed";
      if (String(detail).includes("AUTH4037") || String(detail).toLowerCase().includes("manage api access")) {
        setPortalError(detail);
      } else {
        toast.error(detail);
      }
    }
    setBusy(false);
  };

  const verifyOtp = async () => {
    if (!otp) { toast.error("OTP required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/gst-reports/auth/otp-verify",
        { gstin, otp: otp.trim() });
      toast.success(`Authenticated · expires ${new Date(r.data.plan_expires_at).toLocaleDateString()}`);
      close(false); onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP verification failed");
    }
    setBusy(false);
  };

  const saveManual = async () => {
    if (gstin.length !== 15) { toast.error("Pick a GSTIN"); return; }
    if (!gstUsername) { toast.error("gst_username required"); return; }
    if (!authToken)   { toast.error("auth_token required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/gst-reports/auth/manual-token",
        { gstin, gst_username: gstUsername, auth_token: authToken.trim(), sek: sek.trim() });
      toast.success(`Token saved · valid ${new Date(r.data.token_expires_at).toLocaleString()}`);
      close(false); onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual-token save failed");
    }
    setBusy(false);
  };

  const noGstins = companyGstins.length === 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="gst-auth-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" /> Authenticate GST Portal
          </DialogTitle>
          <DialogDescription>
            One-time login unlocks 30 days of live GST reports. Auto-refreshed
            every 5.5 h in the background.
          </DialogDescription>
        </DialogHeader>

        {noGstins ? (
          <div className="p-4 rounded bg-amber-500/10 border border-amber-500/40 text-sm space-y-3">
            <div className="flex items-center gap-2 font-medium">
              <Settings2 className="w-4 h-4" /> No GSTINs on this company
            </div>
            <div className="text-xs">
              You haven't added a GSTIN yet. Add one at{" "}
              <b>Settings → Company GSTINs</b>, then come back here to authenticate.
            </div>
            <Button variant="outline" size="sm" className="w-full"
                      onClick={() => { close(false); window.location.href = "/app/settings/company-gstins"; }}
                      data-testid="btn-go-settings">
              Go to Settings
            </Button>
          </div>
        ) : step === 1 && mode === "otp" ? (
          <div className="space-y-4">
            {portalError && (
              <div className="p-3 rounded bg-red-500/10 border border-red-500/40 text-xs space-y-2"
                     data-testid="portal-error-banner">
                <div className="flex items-center gap-2 font-semibold text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-4 h-4" /> Enable API access on gst.gov.in
                </div>
                <pre className="whitespace-pre-wrap font-sans text-[11px] leading-relaxed">{portalError}</pre>
                <a href="https://services.gst.gov.in/services/login" target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-primary hover:underline"
                     data-testid="link-gst-portal">
                  Open gst.gov.in <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
            <div>
              <Label>GSTIN (from registered company GSTINs)</Label>
              <select value={gstin} onChange={(e) => setGstin(e.target.value)}
                        className="w-full mt-1 border rounded px-2 py-2 font-mono text-sm bg-background"
                        data-testid="input-gstin-picker">
                {companyGstins.map((g) => (
                  <option key={g.gstin} value={g.gstin}>
                    {g.gstin} · {g.label}{g.is_default ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>GST Portal Username</Label>
              <Input value={gstUsername} onChange={(e) => setGstUsername(e.target.value)}
                       placeholder="Your gst.gov.in login username"
                       data-testid="input-gst-username" />
              <p className="text-xs text-muted-foreground mt-1">
                This is the username you use on gst.gov.in — NOT your WhiteBooks email.
              </p>
            </div>
            <div className="text-xs text-center pt-1">
              <button onClick={() => setMode("manual")} className="text-primary hover:underline"
                        data-testid="switch-to-manual">
                <Wrench className="w-3 h-3 inline mr-1" />
                OTP flow blocked? Paste a token directly instead
              </button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
              <Button onClick={sendOtp} disabled={busy} data-testid="btn-send-otp">
                <Send className="w-4 h-4 mr-2" />
                {busy ? "Dispatching…" : "Send OTP"}
              </Button>
            </DialogFooter>
          </div>
        ) : step === 1 && mode === "manual" ? (
          <div className="space-y-4">
            <div className="p-3 rounded bg-amber-500/10 border border-amber-500/40 text-xs">
              Bypass OTP: paste a WhiteBooks auth_token you obtained elsewhere.
              We'll use it for reports directly — up to 6 hours until refresh.
            </div>
            <div>
              <Label>GSTIN</Label>
              <select value={gstin} onChange={(e) => setGstin(e.target.value)}
                        className="w-full mt-1 border rounded px-2 py-2 font-mono text-sm bg-background"
                        data-testid="input-manual-gstin-picker">
                {companyGstins.map((g) => (
                  <option key={g.gstin} value={g.gstin}>
                    {g.gstin} · {g.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>GST Portal Username</Label>
              <Input value={gstUsername} onChange={(e) => setGstUsername(e.target.value)}
                       placeholder="Your gst.gov.in login username"
                       data-testid="input-manual-gst-username" />
            </div>
            <div>
              <Label>auth_token</Label>
              <Input value={authToken} onChange={(e) => setAuthToken(e.target.value)}
                       placeholder="WhiteBooks auth_token"
                       className="font-mono text-xs" data-testid="input-manual-token" />
            </div>
            <div>
              <Label>Sek (optional)</Label>
              <Input value={sek} onChange={(e) => setSek(e.target.value)}
                       placeholder="(optional)" className="font-mono text-xs"
                       data-testid="input-manual-sek" />
            </div>
            <div className="text-xs text-center pt-1">
              <button onClick={() => setMode("otp")} className="text-primary hover:underline"
                        data-testid="switch-to-otp">
                ← Back to OTP flow
              </button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
              <Button onClick={saveManual} disabled={busy} data-testid="btn-save-manual">
                {busy ? "Saving…" : "Save Token"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* Step 2: verify OTP */
          <div className="space-y-4">
            <div className="p-4 rounded bg-emerald-500/10 border border-emerald-500/40 text-sm">
              OTP dispatched to the mobile/email registered on GST portal for
              <span className="font-mono ml-1">{gstin}</span>.
            </div>
            <div>
              <Label>Enter OTP</Label>
              <Input value={otp} onChange={(e) => setOtp(e.target.value)}
                       maxLength={6} placeholder="6-digit OTP"
                       className="font-mono text-lg tracking-widest text-center"
                       data-testid="input-otp" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={verifyOtp} disabled={busy} data-testid="btn-verify-otp">
                {busy ? "Verifying…" : "Verify & Activate"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
